import { SxProps, Theme } from "@mui/material";
import { MouseEventHandler, ReactElement, ReactNode, useCallback } from "react";
import { SrDialog } from "./SrDialog";

/**
 * Props for the ConfirmationDialog component.
 * @isOpen Whether the dialog is open.
 * @title The title of the dialog.
 * @content The content of the dialog.
 * @labelCancel The label for the cancel button.
 * @labelSecondaryAction The label for a custom button instead of the cancel button.
 * @labelConfirm The label for the confirm button.
 * @colorConfirm The color of the confirm button.
 * @onCallback The (optional) async callback to perform before onClose, will block the dialog from closing.
 * @onClose The callback to close the dialog.
 * @onSecondaryAction The callback for the secondary button's action.
 * @withCloseButton Whether to show an X button in the top right corner.
 */
export type ConfirmationDialogProps = {
  isOpen: boolean;
  name: string;
  title: string;
  content: ReactNode;
  labelCancel?: string;
  labelConfirm: string;
  colorConfirm?: "primary" | "error";
  withCloseButton?: boolean;
  onClose(result?: boolean): void | Promise<void>;
  onClick?: MouseEventHandler<HTMLDivElement>;
  sx?: SxProps<Theme>;

  /** @deprecated use `onClose(true)` instead */
  onCallback?: (result?: boolean) => Promise<void>;
  /** @deprecated use `labelCancel` instead */
  labelSecondaryAction?: string;
  /** @deprecated use `onClose(false)` instead */
  onSecondaryAction?(): void;
};

/**
 * A standardised confirmation dialog, can be used with `useDisclosure` hook (preferred) or, `useConfirmation` hook.
 *
 * @example
 * // Blocking with async onClose (show loading spinner on dialog button before closing) - preferred
 * const { isOpen, onOpen, onClose } = useDisclosure();
 * const onDelete = useDelete();
 *
 * const handleClose = async (confirmed: boolean) => {
 *   if (confirmed) await onDelete();
 *   onClose();
 * };
 *
 * return (
 *   <>
 *    <Button onClick={onOpen}>Delete</Button>
 *    <ConfirmationDialog
 *      isOpen={isOpen}
 *      onClose={handleClose}
 *      title="Are you sure?"
 *      content="This action cannot be undone."
 *      labelCancel="Cancel"
 *      labelConfirm="Delete"
 *      colorConfirm="error"
 *    />
 *   </>
 * );
 *
 * @example
 * // Non-blocking close (show loading spinner on the button that triggered the dialog)
 * const { isOpen, onOpen, onClose } = useConfirmation<boolean>();
 * const onDelete = useDelete();
 *
 * const handleDelete = async () => {
 *   if (await onOpen()) await onDelete();
 * };
 *
 * return (
 *   <>
 *     <Button onClick={handleDelete}>Delete</Button>
 *     <ConfirmationDialog
 *       isOpen={isOpen}
 *       onClose={onClose}
 *       title="Are you sure?"
 *       content="This action cannot be undone."
 *       labelCancel="Cancel"
 *       labelConfirm="Delete"
 *       colorConfirm="error"
 *     />
 *   </>
 * );
 */
export const ConfirmationDialog = ({
  name,
  title,
  content,
  isOpen,
  labelCancel,
  labelConfirm,
  colorConfirm,
  onClose,
  withCloseButton,
  onClick,
  sx,
  onCallback,
  labelSecondaryAction,
  onSecondaryAction,
}: ConfirmationDialogProps): ReactElement => {
  const handleClose = useCallback(
    async (result: boolean) => {
      if (result) await onCallback?.(result);
      await onClose(result);
    },
    [onCallback, onClose],
  );

  return (
    <SrDialog
      name={name}
      open={isOpen}
      withCloseButton={withCloseButton}
      onClick={onClick}
      sx={[
        {
          ".MuiDialog-paper": {
            width: "100%",
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <SrDialog.Title onClose={() => handleClose(false)}>{title}</SrDialog.Title>
      <SrDialog.Content>{content}</SrDialog.Content>
      <SrDialog.Actions>
        {onSecondaryAction ? (
          <SrDialog.Action name="secondary" color="secondary" onClick={onSecondaryAction}>
            {labelSecondaryAction || "Secondary Action"}
          </SrDialog.Action>
        ) : (
          <SrDialog.Action name="cancel" color="secondary" onClick={() => handleClose(false)}>
            {labelCancel || "Cancel"}
          </SrDialog.Action>
        )}
        <SrDialog.Action name="confirm" color={colorConfirm} onClick={() => handleClose(true)}>
          {labelConfirm}
        </SrDialog.Action>
      </SrDialog.Actions>
    </SrDialog>
  );
};
